import {useEffect, useRef, useState} from 'react';

import {cn} from '../../lib/utils';

import type {ReactNode} from 'react';

interface AnimatedHeightProps {
  children: ReactNode;
  className?: string;
}

/**
 * Wraps content and smoothly animates height changes using a ResizeObserver.
 * Works with `height: auto` content by measuring real pixel heights and
 * transitioning between them explicitly.
 */
export function AnimatedHeight({children, className}: AnimatedHeightProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setHeight(entry.contentRect.height);
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      className={cn('overflow-hidden transition-[height] duration-200 ease-in-out', className)}
      style={{height}}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
